import LanguageIcon from '@mui/icons-material/Language'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import { Alert, Button, CircularProgress, Menu, MenuItem, TextField, ThemeProvider, Typography } from '@mui/material'
import { createTheme, styled, keyframes } from '@mui/material/styles'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { Navigate } from 'react-router'
import { getAccessToken, loginWithOrganization, logout } from '../oidc'
import { XxllncAiLogo } from '../components/Icons'
import { BrandingProvider, PortalBranding, useBranding } from '../contexts/BrandingContext'
import { border, color, themeColor, theme as xxTheme } from '../layout/themes'
import authTokenUtil from '../utils/authToken'
import { getEnvVariable } from '../utils/getEnvVariable'
import { tokenHasAdminRole } from '../utils/tokenRole'
import { storeUserDataInLocalStorage } from '../utils/userDetails'
import { LOCALE_KEY } from '../providers'
import { LoginInfoProbeOut } from '../types/apiRequests'

const getApiUrl = (): string => getEnvVariable('REACT_APP_SERVER_URL', '')

const REGIONS = [
  { code: 'en', label: 'English (US)' },
  { code: 'nl', label: 'Nederlands (NL)' },
  { code: 'no', label: 'Norsk (NO)' },
]

const buildTheme = (branding: PortalBranding) => ({
  ...xxTheme,
  palette: {
    ...xxTheme.palette,
    ...(branding.primary_color ? {
      primary: {
        main: branding.primary_color,
        contrastText: '#fff',
      },
    } : {}),
    ...(branding.secondary_color ? {
      secondary: {
        main: branding.secondary_color,
        contrastText: '#fff',
      },
    } : {}),
  },
})

type LoginStep = 'email' | 'loading' | 'noOrg' | 'denied'

const LoginPage: FC = () => {
  const translate = useTranslate()
  const branding = useBranding()
  const [isReady, setIsReady] = useState(false)
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [email, setEmail] = useState<string>('')
  const [logoutReason, setLogoutReason] = useState<string | null>(null)
  const [step, setStep] = useState<LoginStep>('email')
  const [regionAnchor, setRegionAnchor] = useState<HTMLElement | null>(null)
  const [isSigningOut, setIsSigningOut] = useState(false)

  const hasLogo = !!(branding.logo_url && branding.logo_url.trim())
  const isBranded = !!branding.admin_title
  const currentLocale = localStorage.getItem(LOCALE_KEY) || 'en'
  const currentRegion = REGIONS.find(r => r.code === currentLocale) || REGIONS[0]

  const portalUrl = getEnvVariable(
    'REACT_APP_PORTAL_URL',
    `${window.location.protocol}//portal-${window.location.host}`,
  )

  useEffect(() => {
    const reason = sessionStorage.getItem('logoutReason')
    if (reason) {
      setLogoutReason(reason)
      sessionStorage.removeItem('logoutReason')
    }
    if (reason === 'noAdmin') {
      // Session already torn down by authProvider.checkAuth; skip silent probe.
      setStep('denied')
      setIsReady(true)
      return
    }
    // Gate the silent probe — without a token it would resurrect a just-logged-out session via KC's SSO cookie.
    const getToken = async () => {
      try {
        if (!authTokenUtil.getToken()) return
        const token = await getAccessToken()
        if (token) {
          if (!tokenHasAdminRole(token)) {
            authTokenUtil.deleteToken()
            setStep('denied')
            return
          }
          authTokenUtil.setToken(token)
          setAuthToken(token)
          storeUserDataInLocalStorage(token)
        }
      } catch {
        // do nothing
      } finally {
        setIsReady(true)
      }
    }

    getToken().catch(() => {
      // do nothing
    })
  }, [])

  const probeLogin = async (emailAddress: string) => {
    setStep('loading')
    try {
      const url = `${getApiUrl()}/login_info?email=${encodeURIComponent(emailAddress)}`
      const response = await fetch(url)
      if (!response.ok) {
        setStep('noOrg')
        return
      }
      const data = (await response.json()) as LoginInfoProbeOut
      if (data.known) {
        // Empty org id → KC resolves it from the authenticated email's verified domain; idp_hint routes SSO users to their broker.
        loginWithOrganization('', emailAddress, data.idp_hint ?? undefined)
        return
      }
      setStep('noOrg')
    } catch {
      setStep('noOrg')
    }
  }

  const onClickLogin = (event: React.SyntheticEvent) => {
    event.preventDefault()
    if (isReady && email && isValidEmail(email)) {
      void probeLogin(email)
    }
  }

  const onBack = () => {
    setStep('email')
  }

  const onChangeInput = (event: React.ChangeEvent<{ value: unknown }>) => {
    setEmail(event.target.value as string)
  }

  const isValidEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value)
  }

  const onChangeRegion = (code: string) => {
    localStorage.setItem(LOCALE_KEY, code)
    setRegionAnchor(null)
    window.location.reload()
  }

  const onSignOutDenied = () => {
    setIsSigningOut(true)
    void logout()
  }

  if (authToken) return <Navigate to="/" />

  return (
    <Root
      className={classes.root}
      style={branding.login_background_color ? { background: branding.login_background_color } : undefined}
    >
      {!branding.login_background_color && (
        <>
          <div className={classes.gridBg} />
          <div className={classes.glow} />
        </>
      )}

      <div className={classes.container}>
        <header className={classes.header}>
          {hasLogo ? (
            <img src={branding.logo_url!} alt="logo" style={{ maxHeight: '2.5rem', maxWidth: 200 }} />
          ) : (
            <>
              <XxllncAiLogo
                className={classes.logo}
                size="lg"
              />
            </>
          )}
        </header>

        <main className={classes.card}>
          {logoutReason === 'inactivity' && (
            <Alert severity="warning" className={classes.alert}>
              {translate('login.inactive')}
            </Alert>
          )}

          {step === 'email' && (
            <form onSubmit={onClickLogin}>
              <Typography className={classes.title}>
                {translate('login.title')}
              </Typography>
              <Typography className={classes.subtitle}>
                {branding.login_subtitle || translate('login.emailHint')}
              </Typography>
              <Typography component="label" htmlFor="login-email" className={classes.inputLabel}>
                {translate('login.emailLabel')}
              </Typography>
              <TextField
                id="login-email"
                className={classes.input}
                error={!!email && !isValidEmail(email)}
                placeholder={translate('login.email')}
                helperText={!!email && !isValidEmail(email) ? translate('login.notValid') : undefined}
                onChange={onChangeInput}
                disabled={!!authToken}
                defaultValue={email}
                autoFocus
              />
              <Button
                variant="contained"
                type="submit"
                className={classes.button}
                endIcon={<KeyboardDoubleArrowRightIcon />}
                sx={{ 'justifyContent': 'space-between', '& .MuiButton-endIcon': { ml: 0 } }}
                fullWidth
              >
                {translate('login.button')}
              </Button>
            </form>
          )}

          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <CircularProgress size={28} sx={{ color: themeColor.blue }} />
              <Typography sx={{ mt: 2, color: themeColor.slate50, fontSize: 14 }}>
                {translate('login.loading')}
              </Typography>
            </div>
          )}

          {step === 'noOrg' && (
            <div>
              <Alert severity="warning" className={classes.alert}>
                {translate('login.noOrganization')}
              </Alert>
              <Button
                variant="text"
                onClick={onBack}
                fullWidth
                sx={{ textTransform: 'none', color: themeColor.slate50, fontSize: 13 }}
              >
                {translate('login.back')}
              </Button>
            </div>
          )}

          {step === 'denied' && (
            <div>
              <Typography className={classes.title}>
                {translate('login.noAdminAccess')}
              </Typography>
              <Typography className={classes.subtitle}>
                {translate('login.noAdminAccessHint')}
              </Typography>
              <Button
                variant="contained"
                onClick={onSignOutDenied}
                disabled={isSigningOut}
                className={classes.button}
                fullWidth
              >
                {isSigningOut
                  ? translate('login.loading')
                  : translate('login.noAdminAccessAction')}
              </Button>
              <a href={portalUrl} className={classes.hubLink} style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
                {translate('login.signInToHub')}
              </a>
            </div>
          )}

          {step === 'email' && !isBranded && (
            <div className={classes.hubSection}>
              <Typography className={classes.hubText}>
                {translate('login.lookingFor')}
              </Typography>
              <a href={portalUrl} className={classes.hubLink}>
                {translate('login.signInToHub')}
              </a>
            </div>
          )}
        </main>
      </div>

      <footer className={classes.footer}>
        <Button
          className={classes.regionButton}
          onClick={(e) => setRegionAnchor(e.currentTarget)}
          startIcon={<LanguageIcon />}
        >
          {currentRegion.label}
        </Button>
        <Menu
          anchorEl={regionAnchor}
          open={Boolean(regionAnchor)}
          onClose={() => setRegionAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          slotProps={{ paper: { className: classes.regionMenu } }}
        >
          {REGIONS.map(r => (
            <MenuItem
              key={r.code}
              selected={r.code === currentLocale}
              onClick={() => onChangeRegion(r.code)}
              sx={{ fontSize: 13 }}
            >
              {r.label}
            </MenuItem>
          ))}
        </Menu>
      </footer>
    </Root>
  )
}

const LoginPageInner: FC = () => {
  const branding = useBranding()
  const dynamicTheme = useMemo(() => buildTheme(branding), [branding])
  return (
    <ThemeProvider theme={createTheme(dynamicTheme)}>
      <LoginPage />
    </ThemeProvider>
  )
}

const LoginWithTheme: FC = () => (
  <BrandingProvider>
    <LoginPageInner />
  </BrandingProvider>
)

const PREFIX = 'xxllncLogin'

const classes = {
  root: `${PREFIX}-root`,
  gridBg: `${PREFIX}-gridBg`,
  glow: `${PREFIX}-glow`,
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  logo: `${PREFIX}-logo`,
  card: `${PREFIX}-card`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  inputLabel: `${PREFIX}-inputLabel`,
  input: `${PREFIX}-input`,
  button: `${PREFIX}-button`,
  orgButton: `${PREFIX}-orgButton`,
  alert: `${PREFIX}-alert`,
  hubSection: `${PREFIX}-hubSection`,
  hubText: `${PREFIX}-hubText`,
  hubLink: `${PREFIX}-hubLink`,
  footer: `${PREFIX}-footer`,
  regionButton: `${PREFIX}-regionButton`,
  regionMenu: `${PREFIX}-regionMenu`,
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

const logoGlow = keyframes`
  0% { filter: drop-shadow(0 0 0px transparent); }
  50% { filter: drop-shadow(0 0 12px rgba(45,62,80,0.2)); }
  100% { filter: drop-shadow(0 0 4px rgba(45,62,80,0.08)); }
`

const iconReveal = keyframes`
  from { opacity: 0; transform: scale(0.7) rotate(-8deg); }
  to { opacity: 1; transform: scale(1) rotate(0deg); }
`

const textSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
`

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    width: '100vw',
    background: themeColor.slate05,
    position: 'relative',
    overflow: 'hidden',
  },
  [`& .${classes.gridBg}`]: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: `
      radial-gradient(1.5px 1.5px at 10% 15%, rgba(45,62,80,0.08) 50%, transparent 50%),
      radial-gradient(1.5px 1.5px at 25% 40%, rgba(45,62,80,0.06) 50%, transparent 50%),
      radial-gradient(1.5px 1.5px at 85% 20%, rgba(45,62,80,0.05) 50%, transparent 50%),
      radial-gradient(1.5px 1.5px at 70% 65%, rgba(45,62,80,0.07) 50%, transparent 50%),
      radial-gradient(1.5px 1.5px at 90% 80%, rgba(45,62,80,0.05) 50%, transparent 50%),
      radial-gradient(1.5px 1.5px at 40% 85%, rgba(45,62,80,0.06) 50%, transparent 50%),
      radial-gradient(1.5px 1.5px at 60% 10%, rgba(45,62,80,0.05) 50%, transparent 50%),
      radial-gradient(1px 1px at 15% 70%, rgba(45,62,80,0.04) 50%, transparent 50%),
      radial-gradient(1px 1px at 50% 50%, rgba(45,62,80,0.03) 50%, transparent 50%),
      radial-gradient(1px 1px at 75% 35%, rgba(45,62,80,0.04) 50%, transparent 50%)
    `,
  },
  [`& .${classes.glow}`]: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(45,62,80,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(74,144,164,0.025) 0%, transparent 50%),
      linear-gradient(42deg, transparent 10%, rgba(45,62,80,0.015) 10.5%, rgba(45,62,80,0.015) 11%, transparent 11.5%),
      linear-gradient(155deg, transparent 25%, rgba(45,62,80,0.012) 25.3%, rgba(45,62,80,0.012) 25.6%, transparent 26%)
    `,
  },
  [`& .${classes.container}`]: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    maxWidth: 400,
    padding: '0 24px',
    flex: 1,
    justifyContent: 'center',
    animation: `${fadeIn} 0.6s ease-out`,
  },
  [`& .${classes.header}`]: {
    marginBottom: 20,
    marginLeft: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  [`& .${classes.logo}`]: {
    height: 40,
    width: 'auto',
    animation: `${logoGlow} 1.8s ease-out 0.6s both`,
    ['& > svg']: {
      animation: `${iconReveal} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both`,
      transformOrigin: 'center center',
      transformBox: 'fill-box',
    },
    ['& > text']: {
      animation: `${textSlideIn} 0.5s ease-out 0.45s both`,
    },
  },
  [`& .${classes.card}`]: {
    width: '100%',
    background: color.white,
    border: '1px solid rgba(0,0,0,0.07)',
    borderRadius: '20px',
    padding: '36px',
    boxSizing: 'border-box',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 12px 32px rgba(0,0,0,0.05)',
  },
  [`& .${classes.title}`]: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: themeColor.black,
    letterSpacing: '-0.02em',
    fontFamily: 'Plus Jakarta Sans, Inter, sans-serif',
    marginBottom: '6px',
    textAlign: 'left',
  },
  [`& .${classes.subtitle}`]: {
    fontSize: '0.95rem',
    color: themeColor.slate50,
    marginBottom: '28px',
    lineHeight: 1.55,
    textAlign: 'left',
  },
  [`& .${classes.inputLabel}`]: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: themeColor.black,
    marginBottom: '8px',
    textAlign: 'left',
  },
  [`& .${classes.input}`]: {
    marginBottom: '16px',
    ['& .MuiInputBase-root']: {
      borderRadius: border.input.radius,
      backgroundColor: color.white,
      color: themeColor.black,
      fontSize: 14,
      ['& input']: {
        padding: '14px 16px',
        ['&::placeholder']: {
          color: themeColor.slate40,
          opacity: 1,
        },
      },
      ['& fieldset']: {
        borderColor: themeColor.slate10,
        transition: 'border-color 0.15s ease',
      },
      ['&:hover fieldset']: {
        borderColor: themeColor.slate20,
      },
      ['&.Mui-focused fieldset']: {
        borderColor: themeColor.blue,
        borderWidth: '1px',
      },
    },
    ['& .MuiInputLabel-root']: {
      color: themeColor.slate50,
      fontSize: 14,
      ['&.Mui-focused']: {
        color: themeColor.blue,
      },
    },
    ['& .MuiFormHelperText-root']: {
      color: '#ef4444',
    },
  },
  [`& .${classes.button}`]: {
    backgroundColor: themeColor.blue,
    color: color.white,
    borderRadius: '999px',
    fontSize: 15,
    fontWeight: 600,
    padding: '12px 22px',
    textTransform: 'none' as const,
    boxShadow: 'none',
    ['&:hover']: {
      backgroundColor: '#3A2068',
      boxShadow: 'none',
    },
    ['&.Mui-disabled']: {
      backgroundColor: themeColor.slate10,
      color: themeColor.slate40,
    },
  },
  [`& .${classes.orgButton}`]: {
    borderColor: themeColor.slate10,
    color: themeColor.blue,
    borderRadius: border.button.radius,
    fontSize: 14,
    fontWeight: 500,
    padding: '12px 24px',
    textTransform: 'none' as const,
    marginBottom: '8px',
    ['&:hover']: {
      borderColor: themeColor.blue,
      backgroundColor: themeColor.slate05,
    },
  },
  [`& .${classes.alert}`]: {
    marginBottom: '16px',
    borderRadius: border.radius.sm,
    fontSize: 13,
  },
  [`& .${classes.hubSection}`]: {
    marginTop: '24px',
    padding: '16px',
    borderRadius: '14px',
    background: 'rgba(0,0,0,0.035)',
    textAlign: 'left',
  },
  [`& .${classes.hubText}`]: {
    fontSize: '0.8rem',
    color: themeColor.slate50,
    marginBottom: '4px',
  },
  [`& .${classes.hubLink}`]: {
    fontSize: '0.8rem',
    color: themeColor.blue,
    fontWeight: 600,
    textDecoration: 'none',
    ['&:hover']: {
      textDecoration: 'underline',
    },
  },
  [`& .${classes.footer}`]: {
    position: 'relative',
    zIndex: 1,
    padding: '24px',
    display: 'flex',
    justifyContent: 'center',
  },
  [`& .${classes.regionButton}`]: {
    color: themeColor.slate40,
    fontSize: '0.75rem',
    textTransform: 'none' as const,
    fontWeight: 400,
    gap: '6px',
    ['& .MuiButton-startIcon']: {
      marginRight: 0,
    },
    ['& .MuiSvgIcon-root']: {
      fontSize: '16px',
    },
    ['&:hover']: {
      color: themeColor.slate50,
      backgroundColor: 'transparent',
    },
  },
  [`& .${classes.regionMenu}`]: {
    background: color.white,
    border: '1px solid ' + themeColor.slate10,
    ['& .MuiMenuItem-root']: {
      color: themeColor.slate80,
      ['&:hover']: {
        backgroundColor: themeColor.slate05,
      },
      ['&.Mui-selected']: {
        backgroundColor: themeColor.blue10,
        color: themeColor.blue,
      },
    },
  },
}))

export default LoginWithTheme
